import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Plus, Trash2, Users, Building2, ChevronRight, Database, Download, RefreshCw } from "lucide-react";

const emptyUser = { email: "", password: "", name: "", role: "member" };
const emptyTenant = { name: "", admin_email: "", admin_password: "", admin_name: "" };

export default function Settings() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const isTenantAdminOnly = user?.role === "tenant_admin";

    return (
        <div>
            <PageHeader
                kicker="Beheer"
                title="Instellingen"
                subtitle={
                    isSuper
                        ? "Klik op een tenant om gebruikers binnen die tenant te beheren."
                        : "Beheer gebruikers binnen je tenant."
                }
                testid="settings-header"
            />
            {isSuper && <TenantsBlock />}
            {isSuper && <BackupsBlock />}
            {isTenantAdminOnly && <UsersBlock />}
            {!isSuper && !isTenantAdminOnly && (
                <div className="text-sm text-ink-500" data-testid="no-settings">
                    Je hebt geen beheerdersrechten in deze tenant.
                </div>
            )}
        </div>
    );
}

function UsersBlock() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [form, setForm] = useState(emptyUser);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/tenant/users");
            setUsers(data);
        } catch (e) {
            console.error("[settings:users] load failed", e);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const save = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await api.post("/tenant/users", form);
            setForm(emptyUser);
            setShowForm(false);
            await load();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const remove = async (u) => {
        if (!window.confirm(`Gebruiker ${u.email} verwijderen?`)) return;
        try {
            await api.delete(`/tenant/users/${u.id}`);
            await load();
        } catch (err) {
            alert(formatApiErrorDetail(err.response?.data?.detail) || "Verwijderen mislukt");
        }
    };

    return (
        <section className="mb-10" data-testid="users-block">
            <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-ink-900">
                    <Users className="h-4 w-4" /> Gebruikers in je tenant
                </h2>
                <button
                    onClick={() => setShowForm((s) => !s)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-florefy-900 text-white rounded-md hover:bg-florefy-800"
                    data-testid="add-user-btn"
                >
                    <Plus className="h-4 w-4" /> Gebruiker toevoegen
                </button>
            </div>
            {showForm && (
                <form
                    onSubmit={save}
                    className="bg-white border border-border rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3"
                    data-testid="user-form"
                >
                    <input
                        type="text"
                        placeholder="Naam"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        className="px-3 py-2 rounded-md border border-border text-sm"
                        data-testid="user-name-input"
                    />
                    <input
                        type="email"
                        placeholder="E-mail"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                        className="px-3 py-2 rounded-md border border-border text-sm"
                        data-testid="user-email-input"
                    />
                    <input
                        type="password"
                        placeholder="Wachtwoord (min. 8 tekens)"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        required
                        minLength={8}
                        className="px-3 py-2 rounded-md border border-border text-sm"
                        data-testid="user-password-input"
                    />
                    <select
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        className="px-3 py-2 rounded-md border border-border text-sm bg-white"
                        data-testid="user-role-select"
                    >
                        <option value="member">Medewerker</option>
                        <option value="tenant_admin">Beheerder</option>
                    </select>
                    {error && (
                        <div className="md:col-span-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                            {error}
                        </div>
                    )}
                    <div className="md:col-span-2 flex gap-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-3 py-1.5 text-sm bg-florefy-900 text-white rounded-md disabled:opacity-60"
                            data-testid="user-save-btn"
                        >
                            {loading ? "Opslaan…" : "Opslaan"}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowForm(false);
                                setForm(emptyUser);
                                setError("");
                            }}
                            className="px-3 py-1.5 text-sm border border-border rounded-md"
                        >
                            Annuleren
                        </button>
                    </div>
                </form>
            )}
            <div className="bg-white border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="users-table">
                    <thead className="bg-ink-50 text-left text-xs uppercase font-mono tracking-wider text-ink-500">
                        <tr>
                            <th className="px-4 py-2">Naam</th>
                            <th className="px-4 py-2">E-mail</th>
                            <th className="px-4 py-2">Rol</th>
                            <th className="px-4 py-2 w-12"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.email}`}>
                                <td className="px-4 py-2">{u.name}</td>
                                <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                                <td className="px-4 py-2">
                                    {u.role === "tenant_admin" ? "Beheerder" : u.role === "super_admin" ? "Super-admin" : "Medewerker"}
                                </td>
                                <td className="px-4 py-2">
                                    {u.id !== me?.id && u.role !== "super_admin" && (
                                        <button
                                            onClick={() => remove(u)}
                                            className="text-ink-500 hover:text-red-600"
                                            data-testid={`delete-user-${u.email}`}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && (
                            <tr>
                                <td colSpan="4" className="px-4 py-6 text-center text-ink-500 text-sm">
                                    Nog geen gebruikers.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function TenantsBlock() {
    const [tenants, setTenants] = useState([]);
    const [form, setForm] = useState(emptyTenant);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/tenants");
            setTenants(data);
        } catch (e) {
            console.error("[settings:tenants] load failed", e);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const save = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await api.post("/admin/tenants", form);
            setForm(emptyTenant);
            setShowForm(false);
            await load();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const remove = async (t) => {
        if (!window.confirm(`Tenant "${t.name}" VOLLEDIG verwijderen incl. alle data?`)) return;
        try {
            await api.delete(`/admin/tenants/${t.id}`);
            await load();
        } catch (err) {
            alert(formatApiErrorDetail(err.response?.data?.detail) || "Verwijderen mislukt");
        }
    };

    return (
        <section className="mb-10" data-testid="tenants-block">
            <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-ink-900">
                    <Building2 className="h-4 w-4" /> Tenants (Super-Admin)
                </h2>
                <button
                    onClick={() => setShowForm((s) => !s)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-florefy-900 text-white rounded-md hover:bg-florefy-800"
                    data-testid="add-tenant-btn"
                >
                    <Plus className="h-4 w-4" /> Tenant aanmaken
                </button>
            </div>
            {showForm && (
                <form
                    onSubmit={save}
                    className="bg-white border border-border rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3"
                    data-testid="tenant-form"
                >
                    <input
                        type="text"
                        placeholder="Tenantnaam (kwekerij)"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        className="px-3 py-2 rounded-md border border-border text-sm"
                        data-testid="tenant-name-input"
                    />
                    <input
                        type="text"
                        placeholder="Naam beheerder"
                        value={form.admin_name}
                        onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                        className="px-3 py-2 rounded-md border border-border text-sm"
                        data-testid="tenant-admin-name-input"
                    />
                    <input
                        type="email"
                        placeholder="E-mail beheerder"
                        value={form.admin_email}
                        onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                        required
                        className="px-3 py-2 rounded-md border border-border text-sm"
                        data-testid="tenant-admin-email-input"
                    />
                    <input
                        type="password"
                        placeholder="Wachtwoord beheerder (min. 8)"
                        value={form.admin_password}
                        onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                        required
                        minLength={8}
                        className="px-3 py-2 rounded-md border border-border text-sm"
                        data-testid="tenant-admin-password-input"
                    />
                    {error && (
                        <div className="md:col-span-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                            {error}
                        </div>
                    )}
                    <div className="md:col-span-2 flex gap-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-3 py-1.5 text-sm bg-florefy-900 text-white rounded-md disabled:opacity-60"
                            data-testid="tenant-save-btn"
                        >
                            {loading ? "Opslaan…" : "Tenant aanmaken"}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowForm(false);
                                setForm(emptyTenant);
                                setError("");
                            }}
                            className="px-3 py-1.5 text-sm border border-border rounded-md"
                        >
                            Annuleren
                        </button>
                    </div>
                </form>
            )}
            <div className="bg-white border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="tenants-table">
                    <thead className="bg-ink-50 text-left text-xs uppercase font-mono tracking-wider text-ink-500">
                        <tr>
                            <th className="px-4 py-2">Naam</th>
                            <th className="px-4 py-2">Gebruikers</th>
                            <th className="px-4 py-2">Producten</th>
                            <th className="px-4 py-2">Bestellingen</th>
                            <th className="px-4 py-2 w-12"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {tenants.map((t) => (
                            <tr
                                key={t.id}
                                className="border-t border-border hover:bg-ink-50/50 cursor-pointer"
                                data-testid={`tenant-row-${t.name}`}
                            >
                                <td className="px-4 py-2">
                                    <Link
                                        to={`/instellingen/tenants/${t.id}`}
                                        className="inline-flex items-center gap-1 text-florefy-900 hover:underline"
                                        data-testid={`open-tenant-${t.name}`}
                                    >
                                        {t.name}
                                        <ChevronRight className="h-3.5 w-3.5" />
                                    </Link>
                                    {t.system && (
                                        <span className="ml-2 text-[10px] font-mono uppercase text-ink-500">systeem</span>
                                    )}
                                </td>
                                <td className="px-4 py-2">{t.user_count}</td>
                                <td className="px-4 py-2">{t.product_count}</td>
                                <td className="px-4 py-2">{t.order_count}</td>
                                <td className="px-4 py-2">
                                    {!t.system && (
                                        <button
                                            onClick={() => remove(t)}
                                            className="text-ink-500 hover:text-red-600"
                                            data-testid={`delete-tenant-${t.name}`}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {tenants.length === 0 && (
                            <tr>
                                <td colSpan="5" className="px-4 py-6 text-center text-ink-500 text-sm">
                                    Nog geen tenants.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}


function BackupsBlock() {
    const [info, setInfo] = useState({ backups: [], retention_days: 7, backup_hour_utc: 3 });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/backups");
            setInfo(data);
        } catch (e) {
            // 5xx-toast wordt door api.js interceptor afgehandeld; log voor debug
            console.warn("[settings:backups] load failed", e);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const makeFresh = async () => {
        setBusy(true);
        try {
            const r = await api.post("/admin/backups");
            toast.success(`Backup gemaakt (${formatBytes(r.data.size_bytes)})`);
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Backup mislukt");
        } finally {
            setBusy(false);
        }
    };

    const downloadFresh = async () => {
        setBusy(true);
        try {
            // Genereren is bewust een POST; downloaden van het daarna bestaande
            // bestand is een pure GET en kan veilig door de browser worden afgehandeld.
            const r = await api.post("/admin/backups");
            await load();
            window.location.href = `${API}/admin/backups/${encodeURIComponent(r.data.filename)}`;
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Back-up maken en downloaden mislukt");
        } finally {
            setBusy(false);
        }
    };

    const downloadExisting = (filename) => {
        window.location.href = `${API}/admin/backups/${encodeURIComponent(filename)}`;
    };

    return (
        <section className="mb-10" data-testid="backups-block">
            <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-ink-900">
                    <Database className="h-4 w-4" /> Back-ups van de database
                </h2>
                <div className="flex gap-2">
                    <button
                        onClick={makeFresh}
                        disabled={busy}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border rounded-md hover:bg-ink-50 disabled:opacity-50"
                        data-testid="backup-create-btn"
                    >
                        <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
                        Nu een back-up maken
                    </button>
                    <button
                        onClick={downloadFresh}
                        disabled={busy}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-florefy-900 text-white rounded-md hover:bg-florefy-800 disabled:opacity-50"
                        data-testid="backup-download-fresh"
                    >
                        <Download className="h-4 w-4" /> Maak + download nu
                    </button>
                </div>
            </div>
            <div className="bg-white border border-border rounded-lg p-4 mb-3 text-xs font-mono text-ink-500">
                Automatisch dagelijks om {String(info.backup_hour_utc).padStart(2, "0")}:00 UTC ·
                {" "}retentie {info.retention_days} dagen · {info.backups.length} back-up{info.backups.length === 1 ? "" : "s"} bewaard
            </div>
            <div className="bg-white border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="backups-table">
                    <thead className="bg-ink-50 text-left text-xs uppercase font-mono tracking-wider text-ink-500">
                        <tr>
                            <th className="px-4 py-2">Bestand</th>
                            <th className="px-4 py-2">Aangemaakt</th>
                            <th className="px-4 py-2">Grootte</th>
                            <th className="px-4 py-2 w-32"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {info.backups.map((b) => (
                            <tr key={b.filename} className="border-t border-border" data-testid={`backup-row-${b.filename}`}>
                                <td className="px-4 py-2 font-mono text-xs">{b.filename}</td>
                                <td className="px-4 py-2 text-xs">{formatDate(b.modified_at)}</td>
                                <td className="px-4 py-2 font-mono text-xs">{formatBytes(b.size_bytes)}</td>
                                <td className="px-4 py-2">
                                    <button
                                        onClick={() => downloadExisting(b.filename)}
                                        className="inline-flex items-center gap-1 text-florefy-900 hover:underline text-xs font-mono uppercase tracking-widest"
                                        data-testid={`backup-download-${b.filename}`}
                                    >
                                        <Download className="h-3.5 w-3.5" /> Download
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {info.backups.length === 0 && (
                            <tr>
                                <td colSpan="4" className="px-4 py-6 text-center text-ink-500 text-sm">
                                    Nog geen back-ups. Klik op &quot;Nu een back-up maken&quot; om de eerste aan te maken.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function formatBytes(n) {
    if (!n) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let v = n;
    while (v >= 1024 && i < units.length - 1) {
        v /= 1024;
        i += 1;
    }
    return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleString("nl-NL", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
